/**
 * The launcher-side state of Iris (the PMS agent widget), as pure functions so the hook
 * stays a thin subscription and this can be tested without a DOM.
 *
 * Source: widget 0.107.0, `docs/widget-integration.md` § 5 "Showing state on your own
 * button" (dataseekers-agents). Every event fires on `document` and its `detail` carries
 * `{ open, busy, unread }`; `dsb:error` adds `offline` (true when the agent was never
 * reached); `dsb:answer` adds `message_id`. `DSB.state()` reads the same three plus
 * `ready`, and is a function: a value read once goes stale, so it is only used to seed.
 */
export const IRIS_EVENTS = ['dsb:ready', 'dsb:open', 'dsb:close', 'dsb:busy', 'dsb:idle', 'dsb:answer', 'dsb:unread', 'dsb:error']

const UNKNOWN = { ready: false, open: false, busy: false, unread: 0, offline: false }

const isBool = v => typeof v === 'boolean'
const isCount = v => typeof v === 'number' && Number.isFinite(v)

// `{ open, busy, unread, ready }` from the widget handle's readers, or null when there is no
// handle yet (the script is async) or it predates the readers
export const readDsbState = dsb => {
	if (!dsb || typeof dsb.state !== 'function') return null
	const s = dsb.state()
	if (!s || typeof s !== 'object') return null
	return { open: Boolean(s.open), busy: Boolean(s.busy), unread: isCount(s.unread) ? s.unread : 0, ready: Boolean(s.ready) }
}

// `setup` (CU-869fkmt6r, UX D15) is the sixth state and comes from the profile, not the widget: the
// user has saved nothing of theirs yet. It is only in the state when the launcher passes it.
export const initialIrisState = (dsb, options) => {
	const seed = readDsbState(dsb)
	const state = seed ? { ...UNKNOWN, ...seed } : { ...UNKNOWN }
	return options && 'setup' in options ? withSetup(state, options.setup) : state
}

export const withSetup = (state, setup) => ({ ...state, setup: Boolean(setup) })

// the three shared fields, taken from a detail only when they are the primitives the guide
// promises (the `dsb:ready` detail is the DSB object itself, whose readers are functions)
const sharedFields = detail => {
	if (!detail || typeof detail !== 'object') return {}
	const next = {}
	if (isBool(detail.open)) next.open = detail.open
	if (isBool(detail.busy)) next.busy = detail.busy
	if (isCount(detail.unread)) next.unread = detail.unread
	return next
}

export const applyIrisEvent = (state, type, detail) => {
	if (!IRIS_EVENTS.includes(type)) return state
	const next = { ...state, ...sharedFields(detail) }
	switch (type) {
		case 'dsb:ready': next.ready = true; break
		case 'dsb:error': next.offline = Boolean(detail && detail.offline); break
		case 'dsb:idle':
		case 'dsb:answer': next.offline = false; break
		default: break
	}
	return next
}

// tooltip and aria-label: offline wins, then an unread answer, then thinking, then "Set up Iris", then the name
export const irisLabel = ({ offline, unread, busy, setup }) => {
	if (offline) return 'Iris — not available'
	if (unread > 0) return `Iris — ${unread} new answer${unread === 1 ? '' : 's'}`
	if (busy) return 'Iris is thinking…'
	if (setup) return 'Set up Iris — Settings › Iris'
	return 'Iris — AI assistant'
}

// the line under the name: offline > Starting… > unread > busy > setup > AI assistant — the nudge never
// hides a health state or an unread answer (UX D15). `key` is what the launcher colours.
export const irisSubline = ({ ready, offline, unread, busy, setup }) => {
	if (offline) return { key: 'offline', text: 'Not available' }
	if (!ready) return { key: 'starting', text: 'Starting…' }
	if (unread > 0) return { key: 'unread', text: `${unread} new answer${unread === 1 ? '' : 's'}` }
	if (busy) return { key: 'busy', text: 'Thinking…' }
	if (setup) return { key: 'setup', text: 'Set up Iris' }
	return { key: 'idle', text: 'AI assistant' }
}
